import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  try {
    let id = localStorage.getItem("pierre-chat-session");
    if (!id) {
      id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("pierre-chat-session", id);
    }
    return id;
  } catch {
    return `s-${Date.now()}`;
  }
};

const ChatWidget = () => {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("assistant");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming || limitReached) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId.current,
          message: text,
          mode,
          lang,
        }),
      });

      if (res.status === 429) {
        setLimitReached(true);
        setMessages((m) => m.slice(0, -1));
        setStreaming(false);
        return;
      }
      if (!res.ok || !res.body) throw new Error("chat failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let failed = false;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) {
              failed = true;
            } else if (parsed.t) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, content: last.content + parsed.t };
                return copy;
              });
            }
          } catch {
            /* partial JSON chunk */
          }
        }
      }
      if (failed) throw new Error("stream error");
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const chips = [
    { id: "assistant", label: t.chat.chips.assistant },
    { id: "intake", label: t.chat.chips.intake },
    { id: "tips", label: t.chat.chips.tips },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={open ? t.chat.closeLabel : t.chat.openLabel}
        data-testid="chat-widget-button"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gold text-black flex items-center justify-center hover:bg-gold-bright transition-colors shadow-lg shadow-black/50"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-4 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[70vh] bg-coal-card border border-line-strong rounded-xl flex flex-col overflow-hidden shadow-2xl shadow-black/60"
          data-testid="chat-panel"
        >
          <div className="px-4 py-3 border-b border-line-subtle bg-coal-raise">
            <div className="font-display uppercase tracking-wider text-white text-sm">
              {t.chat.title}
            </div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-gold-dim mt-0.5">
              {t.chat.subtitle}
            </div>
          </div>

          <div className="flex gap-2 px-3 py-2 border-b border-line-subtle overflow-x-auto">
            {chips.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setMode(c.id)}
                data-testid={`chat-chip-${c.id}`}
                className={`whitespace-nowrap text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                  mode === c.id
                    ? "bg-gold text-black border-gold font-semibold"
                    : "text-bone-soft border-line-strong hover:border-gold/60 hover:text-white"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-xs text-bone-mute text-center pt-8 px-4">{t.chat.subtitle}</div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gold text-black"
                      : "bg-coal-raise text-bone border border-line-subtle"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
            {limitReached && (
              <div className="text-xs text-gold-dim text-center" data-testid="chat-limit-notice">
                {t.chat.limitReached}
              </div>
            )}
          </div>

          <div className="p-3 border-t border-line-subtle flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t.chat.placeholder}
              maxLength={1000}
              disabled={limitReached}
              data-testid="chat-input"
              className="flex-1 bg-coal-field border border-line-field rounded px-3 py-2.5 text-sm text-white placeholder:text-bone-mute/60 focus:outline-none focus:border-gold transition-colors"
            />
            <button
              type="button"
              onClick={send}
              disabled={streaming || !input.trim() || limitReached}
              aria-label={t.chat.send}
              data-testid="chat-send-button"
              className="px-4 rounded bg-gold text-black hover:bg-gold-bright transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
